import React from "react";
import { NavLink } from "react-router-dom";
import { TableCell, Tooltip } from "@heroui/react";

import OrderStatusBadge from "./OrderStatusBadge";
import Deadline from "./Deadline";
import StartDateTime from "./StartDateTime";
import OrderDeliveryDate from "./OrderDeliveryDate";
import OrderStatusIndicator from "./OrderStatusIndicator";
import Assignement from "./Assignement";
import SecondaryActions from "./SecondaryActions";
import DuplicateIndicator from "./DuplicateIndicator";
import InternalNoteIndicator from "./InternalNoteIndicator";
import WarningsCellContent from "./WarningsCellContent";
import type { Order, CellResult, RenderOrderCellProps } from "./types";

function renderStatusContent(
  order: Order,
  isActive: boolean,
  isChildRow: boolean,
  t: (key: string) => string
): React.ReactNode {
  const statusBadge = (
    <>
      <OrderStatusBadge status={order.status} interactionHistory={order.interactionHistory} />
      {!isChildRow && order.duplicate_job_ids?.length > 0 && (
        <DuplicateIndicator duplicates={order.duplicate_job_ids} isActive={isActive} t={t} />
      )}
      {order.internalNote && <InternalNoteIndicator note={order.internalNote} t={t} />}
    </>
  );

  if (isActive) {
    return (
      <Tooltip content={t("processing_inprogress")}>
        <div className="text-bold text-tiny text-default-400 flex cursor-not-allowed">
          {statusBadge}
        </div>
      </Tooltip>
    );
  }

  return (
    <NavLink className="text-bold text-tiny text-default-500 flex" to={`/orders/${order.jobID}`}>
      {statusBadge}
    </NavLink>
  );
}

function renderSubjectContent(
  order: Order,
  cellValue: string,
  isActive: boolean,
  isChildRow: boolean,
  t: (key: string) => string
): React.ReactNode {
  const displayValue =
    isChildRow && order.groupTotal && order.groupTotal > 1
      ? `${order.groupIndex}/${order.groupTotal} ${t("multiple_order")}`
      : cellValue;

  if (isActive) {
    return (
      <Tooltip content={t("processing_inprogress")}>
        <div className="text-bold text-tiny text-default-400 cursor-not-allowed flex items-center">
          {displayValue}
        </div>
      </Tooltip>
    );
  }

  return (
    <NavLink className="text-bold text-tiny text-default-500 flex items-center" to={`/orders/${order.jobID}`}>
      {displayValue}
    </NavLink>
  );
}

export function getOrderCellData({
  order,
  columnKey,
  columns,
  entities,
  isDataReadOnlyOperators,
  setUnreadOrders,
  isChildRow = false,
  t,
}: RenderOrderCellProps): CellResult {
  const cellValue = order[columnKey as keyof Order];
  const columnDef = columns.find((col) => col.uid === columnKey);
  let columnStyle = columnDef?.style || {};

  if (isChildRow && columnKey === "status") {
    columnStyle = { width: "145px", minWidth: "145px", maxWidth: "145px" };
  }

  let cellClass = "";
  if (!order.hasUserMarkedAsUnread) {
    cellClass += " font-bold";
  }

  const isActive = order.status === "active";

  const renderActiveWrapper = (content: React.ReactNode) => {
    if (isActive) {
      return (
        <Tooltip content={t("processing_inprogress")}>
          <div className="text-bold text-[10px] text-default-400 cursor-not-allowed">
            {content}
          </div>
        </Tooltip>
      );
    }
    return (
      <NavLink
        className="text-bold text-[10px] text-default-500"
        to={`/orders/${order.jobID}`}
      >
        {content}
      </NavLink>
    );
  };

  let content: React.ReactNode = null;

  switch (columnKey) {
    case "customer_name":
      content = renderActiveWrapper(
        <div className="flex flex-col">
          <p className={`text-bold text-tiny ${isActive ? "text-default-400" : "text-default-500"}`}>
            {order.customer_name}
          </p>
        </div>
      );
      break;

    case "status":
      content = renderStatusContent(order, isActive, isChildRow, t);
      break;

    case "assignment":
      content = (
        <div className="flex gap-2">
          {order.status !== "success" && (
            <Assignement
              currentAssignementId={order.assignment_id ?? null}
              orderId={order.jobID}
              entityId={order.entity_id ?? null}
            />
          )}
        </div>
      );
      break;

    case "order_status_indicator":
      content = <OrderStatusIndicator orderId={order.jobID} viewers={[]} />;
      break;

    case "deadline":
      content = <Deadline orderStatus={order.status}>{order.deadline}</Deadline>;
      break;

    case "start_datetime":
      content = <StartDateTime start_datetime={order.start_datetime} />;
      break;

    case "order_delivery_date":
      content = <OrderDeliveryDate order_delivery_date={order.order_delivery_date} />;
      break;

    case "actions":
      content = isDataReadOnlyOperators ? null : (
        <SecondaryActions orderId={order.jobID} setUnreadOrders={setUnreadOrders} />
      );
      break;

    case "warnings":
      content = order.status === "success" ? null : (
        <WarningsCellContent warnings={cellValue as number} orderId={order.jobID} isActive={isActive} t={t} />
      );
      break;

    case "entity_name": {
      const matchingEntity = entities.find((entity) => entity.entity_id === order.entity_id);
      content = matchingEntity ? (
        <div className="flex items-center">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: matchingEntity.entity_color }}
          >
            {matchingEntity.entity_initials}
          </div>
        </div>
      ) : null;
      break;
    }

    case "subject":
      content = renderSubjectContent(order, cellValue as string, isActive, isChildRow, t);
      break;

    default:
      content = renderActiveWrapper(cellValue as React.ReactNode);
      break;
  }

  return { content, className: cellClass, style: columnStyle };
}

export function renderOrderCell(props: RenderOrderCellProps): React.ReactElement {
  const { content, className, style } = getOrderCellData(props);
  return (
    <TableCell className={className} style={style}>
      {content}
    </TableCell>
  );
}

export default renderOrderCell;
